"use client";

import { useEffect, useId, useRef, useState } from "react";
import { useSitePathname } from "@/lib/i18n/useSitePathname";
import { ChevronDown, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { LOCALES, PREFIXED_LOCALES, type Locale } from "@/lib/i18n/translations";
import { useTranslation } from "@/lib/i18n/LanguageContext";
import { track } from "@/lib/analytics";

/** Strips the locale prefix from a path: "/en/preise" -> "/preise". */
function stripLocalePrefix(path: string): string {
  for (const l of PREFIXED_LOCALES) {
    if (path === `/${l}`) return "/";
    if (path.startsWith(`/${l}/`)) return path.slice(l.length + 1);
  }
  return path;
}

/** Prepends the locale prefix for a target language; German uses no prefix. */
function withLocalePrefix(locale: Locale, path: string): string {
  if (locale === "de") return path;
  if (path === "/") return `/${locale}`;
  return `/${locale}${path}`;
}

type Props = {
  variant?: "light" | "dark";
  className?: string;
  /**
   * "menu": the desktop header — the current flag and code as a small button
   * that opens a panel with every language. "grid": every language as a flag
   * with its own name, two per row. "compact": the phone menu — one row with
   * the current language that opens that grid. Anything else renders the
   * plain dropdown (admin).
   */
  layout?: "menu" | "grid" | "segmented" | "compact";
};

/**
 * Language flags. Arabic: the flag of the United Arab Emirates, in the
 * pan-Arab colours, as the operator asked for a flag (03.10.2026); not the
 * Saudi flag, which carries the shahada and should not be printed as a
 * decorative icon. Russian: the Russian flag (operator, 03.10.2026).
 */
function Flag({ code, className }: { code: string; className?: string }) {
  const cls = cn("shrink-0 overflow-hidden rounded-[3px] shadow-[0_0_0_0.5px_rgba(0,0,0,0.18)]", className ?? "h-[14px] w-[20px]");
  switch (code) {
    case "de":
      return (
        <svg viewBox="0 0 16 11" className={cls} aria-hidden preserveAspectRatio="none">
          <rect width="16" height="3.67" fill="#000" />
          <rect y="3.67" width="16" height="3.67" fill="#DD0000" />
          <rect y="7.34" width="16" height="3.67" fill="#FFCE00" />
        </svg>
      );
    case "en":
      return (
        <svg viewBox="0 0 60 40" className={cls} aria-hidden preserveAspectRatio="none">
          <rect width="60" height="40" fill="#012169" />
          <path d="M0,0 L60,40 M60,0 L0,40" stroke="#FFFFFF" strokeWidth="6" />
          <path d="M0,0 L60,40 M60,0 L0,40" stroke="#C8102E" strokeWidth="2.5" />
          <rect x="25" width="10" height="40" fill="#FFFFFF" />
          <rect y="15" width="60" height="10" fill="#FFFFFF" />
          <rect x="27" width="6" height="40" fill="#C8102E" />
          <rect y="17" width="60" height="6" fill="#C8102E" />
        </svg>
      );
    case "da":
      return (
        <svg viewBox="0 0 16 11" className={cls} aria-hidden preserveAspectRatio="none">
          <rect width="16" height="11" fill="#C8102E" />
          <rect x="5" width="2" height="11" fill="#FFF" />
          <rect y="4.5" width="16" height="2" fill="#FFF" />
        </svg>
      );
    case "ar":
      return (
        <svg viewBox="0 0 16 11" className={cls} aria-hidden preserveAspectRatio="none">
          <rect width="16" height="3.67" fill="#00732F" />
          <rect y="3.67" width="16" height="3.67" fill="#FFFFFF" />
          <rect y="7.34" width="16" height="3.67" fill="#000000" />
          <rect width="4" height="11" fill="#FF0000" />
        </svg>
      );
    case "uk":
      return (
        <svg viewBox="0 0 16 11" className={cls} aria-hidden preserveAspectRatio="none">
          <rect width="16" height="5.5" fill="#0057B7" />
          <rect y="5.5" width="16" height="5.5" fill="#FFD700" />
        </svg>
      );
    case "ru":
      return (
        <svg viewBox="0 0 16 11" className={cls} aria-hidden preserveAspectRatio="none">
          <rect width="16" height="3.67" fill="#FFFFFF" />
          <rect y="3.67" width="16" height="3.67" fill="#0039A6" />
          <rect y="7.34" width="16" height="3.67" fill="#D52B1E" />
        </svg>
      );
    case "vi":
      return (
        <svg viewBox="0 0 16 11" className={cls} aria-hidden preserveAspectRatio="none">
          <rect width="16" height="11" fill="#DA251D" />
          <polygon points="8,2.2 8.85,4.75 11.5,4.75 9.35,6.3 10.15,8.85 8,7.3 5.85,8.85 6.65,6.3 4.5,4.75 7.15,4.75" fill="#FFFF00" />
        </svg>
      );
    case "es":
      // Spain, the civil flag without the coat of arms (red, yellow 1:2:1).
      return (
        <svg viewBox="0 0 16 11" className={cls} aria-hidden preserveAspectRatio="none">
          <rect width="16" height="11" fill="#AA151B" />
          <rect y="2.75" width="16" height="5.5" fill="#F1BF00" />
        </svg>
      );
    default:
      return null;
  }
}

export function LanguageSwitcher({ variant = "dark", className, layout }: Props) {
  const { locale, setLocale, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const pathname = useSitePathname();

  const isDark = variant === "dark";
  const current = LOCALES.find((l) => l.code === locale) ?? LOCALES[0];

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const switchTo = (code: Locale) => {
    setOpen(false);
    if (code === locale) return;
    track("locale_change", { from: locale, to: code });
    // Writes the cookie before leaving, so the server renders the new
    // language (unprefixed German URLs included).
    setLocale(code);
    // A full page load, not router.push: /x and /en/x are rewritten to the
    // same route, so a client navigation reused the cached server parts and
    // guide text, metadata and JSON-LD stayed in the old language. Query and
    // hash are kept (/buchen?pkg=essential), and the phone menu closes with
    // the reload.
    const path = withLocalePrefix(code, stripLocalePrefix(pathname));
    const { search, hash } = window.location;
    // Same path (e.g. English on an unprefixed page back to German): assign()
    // with a hash would only jump to the anchor, so reload instead.
    if (path === window.location.pathname) window.location.reload();
    else window.location.assign(path + search + hash);
  };

  /**
   * One language as a real link, so search engines can follow every page to
   * each language version from any page (hreflang in the head says the same);
   * the click itself goes through switchTo, which also keeps query and hash.
   */
  const linkProps = (code: Locale) => ({
    href: withLocalePrefix(code, stripLocalePrefix(pathname)),
    hrefLang: code,
    "aria-current": code === locale ? ("true" as const) : undefined,
    onClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
      // Ctrl/Cmd/Shift-click opens the link as usual.
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      switchTo(code);
    },
  });

  // Every language with its flag and its own name, two per row, full tap
  // height. The current one is marked, not hidden.
  const tiles = (extra?: string) => (
      <div role="group" aria-label={t("common", "languageLabel")} className={cn("grid grid-cols-2 gap-2", extra)}>
        {LOCALES.map((l) => {
          const active = l.code === locale;
          return (
            <a
              key={l.code}
              {...linkProps(l.code)}
              className={cn(
                "flex min-h-11 items-center gap-2.5 rounded-xl border px-3 py-2 text-[14px] transition-colors",
                active
                  ? "border-coral-300 bg-coral-50/70 font-semibold text-coral-700"
                  : "border-navy-100 bg-white text-navy-800 hover:border-navy-200",
              )}
            >
              <Flag code={l.code} className="h-[15px] w-[22px]" />
              <span lang={l.code} className="native-name truncate">{l.label}</span>
              {active && <Check className="ms-auto h-4 w-4 shrink-0 text-coral-600" strokeWidth={2.5} aria-hidden />}
            </a>
          );
        })}
      </div>
  );

  if (layout === "grid") return tiles(className);

  if (layout === "compact") {
    // Phone menu: one row with the current language; it opens the seven
    // tiles. All seven open took four rows, and the menu links went below the
    // fold (operator, 03.10.2026). <details> keeps every language link in the
    // HTML, so crawlers still find each version.
    return (
      <details className={cn("group", className)}>
        <summary className="flex min-h-11 cursor-pointer list-none items-center gap-2.5 rounded-xl border border-navy-100 bg-white px-3 py-2 text-[14px] text-navy-800 transition-colors hover:border-navy-200 [&::-webkit-details-marker]:hidden">
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-navy-500">{t("common", "languageLabel")}</span>
          <span className="ms-auto flex items-center gap-2">
            <Flag code={current.code} className="h-[15px] w-[22px]" />
            <span lang={current.code} className="native-name font-semibold">{current.label}</span>
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-navy-500 transition-transform duration-200 group-open:rotate-180" aria-hidden />
        </summary>
        {tiles("mt-2")}
      </details>
    );
  }

  if (layout === "menu") {
    // Desktop header: the current language as a small pill; the panel lists
    // all seven with flag and name. The panel stays in the DOM (hidden), so
    // its links are there for crawlers on every page.
    return (
      <div ref={wrapperRef} className={cn("relative", className)}>
        <button
          type="button"
          // The accessible name contains the visible code (label-content-name).
          aria-label={`${t("common", "languageLabel")}: ${current.flag} – ${current.label}`}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((v) => !v)}
          className={cn(
            "inline-flex h-9 items-center gap-2 rounded-full border border-navy-100 bg-white/80 px-3 text-[11.5px] font-semibold uppercase tracking-[0.14em] text-navy-800 shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition-colors hover:border-coral-300 hover:text-coral-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral-300",
            open && "border-coral-300",
          )}
        >
          <Flag code={current.code} />
          <span>{current.flag}</span>
          <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} strokeWidth={2.2} aria-hidden />
        </button>
        <div
          id={panelId}
          hidden={!open}
          className="absolute start-0 top-[calc(100%+0.5rem)] z-50 w-60 rounded-2xl border border-navy-100 bg-white p-1.5 shadow-[var(--shadow-premium)]"
          style={open ? { animation: "var(--animate-fade-up)", animationDuration: "160ms" } : undefined}
        >
          <ul aria-label={t("common", "languageLabel")}>
            {LOCALES.map((l) => {
              const active = l.code === locale;
              return (
                <li key={l.code}>
                  <a
                    {...linkProps(l.code)}
                    className={cn(
                      "flex items-center gap-3 rounded-xl px-3 py-2.5 text-[14px] transition-colors",
                      active ? "bg-coral-50/70 font-semibold text-coral-700" : "text-navy-800 hover:bg-cream-100",
                    )}
                  >
                    <Flag code={l.code} className="h-[15px] w-[22px]" />
                    <span lang={l.code} className="native-name flex-1">{l.label}</span>
                    {active ? (
                      <Check className="h-4 w-4 text-coral-600" strokeWidth={2.5} aria-hidden />
                    ) : (
                      <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-navy-400">{l.flag}</span>
                    )}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className={cn("relative inline-block", className)}>
      <button
        type="button"
        // The aria-label must contain the visible short code, otherwise voice
        // control cannot target the button (label-content-name-mismatch).
        aria-label={`${t("common", "languageLabel")}: ${current.flag}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "inline-flex h-11 items-center gap-2 rounded-lg border px-3 text-[12.5px] font-semibold uppercase tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
          isDark
            ? "border-cream-50/35 bg-cream-50/12 text-cream-50 hover:bg-cream-50/20 focus-visible:ring-cream-50/60 focus-visible:ring-offset-navy-900"
            : "border-navy-200 bg-white text-navy-700 shadow-[var(--shadow-soft)] hover:border-coral-300 hover:text-coral-700 focus-visible:ring-coral-300 focus-visible:ring-offset-cream-50",
        )}
      >
        <Flag code={current.code} />
        <span className="leading-none">{current.flag}</span>
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} strokeWidth={2} />
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={t("common", "languageLabel")}
          className="absolute end-0 top-[calc(100%+0.4rem)] z-50 min-w-[12rem] overflow-hidden rounded-lg border border-navy-100 bg-white shadow-[var(--shadow-premium)]"
          style={{ animation: "var(--animate-fade-up)", animationDuration: "160ms" }}
        >
          {LOCALES.map((l) => {
            const active = l.code === locale;
            return (
              <li key={l.code} role="option" aria-selected={active}>
                <button
                  type="button"
                  onClick={() => switchTo(l.code as Locale)}
                  className={cn(
                    "flex min-h-[44px] w-full items-center gap-3 px-3.5 py-3 text-start text-[13.5px] transition-colors",
                    active ? "bg-coral-50/70 text-coral-700" : "text-navy-700 hover:bg-cream-100 hover:text-coral-700",
                  )}
                >
                  <Flag code={l.code} />
                  <span lang={l.code} className="native-name flex-1 font-medium">{l.label}</span>
                  {active && <Check className="h-4 w-4 text-coral-600" strokeWidth={2.5} />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
